<template>
  <el-dialog
    :title="$t('sections.alarmSettings.modal.title')"
    :visible.sync="showModal"
    width="40%"
    :before-close="handleDialogClose"
    :key="`alarm-settings-modal-${key}`"
  >
    <div class="row">
      <div class="d-flex flex-column align-items-end col-auto">
        <HorizontalDetailTitle
          :label="$t('sections.alarmSettings.modal.module')"
        />
        <HorizontalDetailTitle
          :label="$t('sections.alarmSettings.modal.alarmId')"
        />
        <HorizontalDetailTitle
          :label="$t('sections.alarmSettings.modal.message')"
        />
      </div>
      <div class="d-flex flex-column col-auto flex-fill">
        <HorizontalDetailInput
          :field="updatedAlarmSetting.module"
          :edit="false"
        />
        <HorizontalDetailInput
          :field="getAlarmId($t('alarmIds'), updatedAlarmSetting.id )"
          :edit="false"
        />
        <HorizontalDetailInput
          :field="updatedAlarmSetting.message"
          edit
          type="textarea"
          @input="(e) => onInput('message', e)"
        />
      </div>
    </div>
    <div class="params-title my-3">
      <span>
        {{ $t("sections.alarmSettings.modal.parameters") }}
      </span>
    </div>
    <div class="row mb-3">
      <div class="d-flex flex-column align-items-end col-auto">
        <HorizontalDetailTitle
          v-for="(param, index) in updatedAlarmSetting.params"
          :key="`detail-title-${index}`"
          :label="param.label"
        />
      </div>
      <div class="d-flex flex-column col-auto flex-fill">
        <HorizontalDetailInput
          v-for="(param, index) in updatedAlarmSetting.params"
          :key="`detail-title-${index}`"
          :field="param.value"
          edit
          type="text"
          @input="(e) => onParamInput(index, e)"
        />
      </div>
    </div>
    <base-table :data="modules">
      <el-table-column
        prop="name"
        :label="$t('sections.alarmSettings.modal.emailTo')"
      />
      <el-table-column width="100%">
        <template slot-scope="props">
          <div class="ml-2">
            <el-checkbox
              v-model="props.row.checked"
              :disabled="allChecked && props.$index !== 3"
              @change="handleCheckboxInput(props.$index)"
            />
          </div>
        </template>
      </el-table-column>
    </base-table>
    <div class="row">
      <div class="d-flex flex-column col-12">
        <span class="required-fields mt-3">
          {{ $t("pages.details.requiredFields") }}
        </span>
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleDialogClose" class="outline-button">
        {{ $t("sections.alarmSettings.modal.cancelButton") }}
      </el-button>
      <el-button
        type="primary"
        @click="onSave"
        :disabled="!wasEdited || !validFields"
      >
        {{ $t("sections.alarmSettings.modal.saveButton") }}
      </el-button>
    </span>
  </el-dialog>
</template>
<script>
import { cloneDeep } from 'lodash';
import HorizontalDetailTitle from '@/components/ui/HorizontalDetailTitle.vue';
import HorizontalDetailInput from '@/components/ui/HorizontalDetailInput.vue';

export default {
  name: 'AlarmSettingsDetailModal',
  components: {
    HorizontalDetailTitle,
    HorizontalDetailInput,
  },
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    alarmSetting: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      key: 0,
      wasEdited: false,
      modules: [
        {
          name: this.$t('sections.alarmSettings.modal.mobie.label'),
          value: this.$t('sections.alarmSettings.modal.mobie.value'),
          checked: false,
        },
        {
          name: this.$t('sections.alarmSettings.modal.emsp.label'),
          value: this.$t('sections.alarmSettings.modal.emsp.value'),
          checked: false,
        },
        {
          name: this.$t('sections.alarmSettings.modal.cpo.label'),
          value: this.$t('sections.alarmSettings.modal.cpo.value'),
          checked: false,
        },
        {
          name: this.$t('sections.alarmSettings.modal.all.label'),
          value: this.$t('sections.alarmSettings.modal.all.value'),
          checked: false,
        },
      ],
      updatedAlarmSetting: {
        module: undefined,
        message: undefined,
        params: [],
        emailGroups: [],
      },
      allChecked: false,
    };
  },
  async mounted() {
    await this.syncAlarmSetting();
  },
  computed: {
    validModule() {
      return (
        this.updatedAlarmSetting.module === undefined
        || this.updatedAlarmSetting.module.length > 0
      );
    },
    validMessage() {
      return (
        this.updatedAlarmSetting.message === undefined
        || this.updatedAlarmSetting.message.length > 0
      );
    },
    validParams() {
      return this.updatedAlarmSetting.params.every(
        (param) => param.value !== null && param.value.length > 0,
      );
    },
    validFields() {
      return (
        this.updatedAlarmSetting.module
        && this.validModule
        && this.updatedAlarmSetting.message
        && this.validMessage
        && this.validParams
        && this.updatedAlarmSetting.emailGroups != null
      );
    },
  },
  watch: {
    async alarmSetting() {
      await this.syncAlarmSetting();
    },
  },
  methods: {
    async syncAlarmSetting() {
      this.updatedAlarmSetting = cloneDeep(this.alarmSetting);
      await Promise.all(
        this.modules.map(async (module) => {
          module.checked = this.updatedAlarmSetting.emailGroups.includes(
            module.value,
          );

          if (
            module.value === this.$t('sections.alarmSettings.modal.all.value')
          ) {
            this.allChecked = module.checked;
          }
        }),
      );
    },
    onInput(field, value) {
      this.wasEdited = true;
      this.updatedAlarmSetting[field] = value;
    },
    onParamInput(index, value) {
      this.wasEdited = true;
      this.updatedAlarmSetting.params[index].value = value;
    },
    handleCheckboxInput(index) {
      this.wasEdited = true;
      if (index === 3) {
        if (this.modules[3].checked) {
          this.modules[0].checked = true;
          this.modules[1].checked = true;
          this.modules[2].checked = true;
        }
        this.allChecked = !this.allChecked;
      }
    },
    async onSave() {
      this.updatedAlarmSetting.emailGroups = [];
      await Promise.all(
        this.modules.map(async (module) => {
          if (module.checked) {
            this.updatedAlarmSetting.emailGroups.push(module.value);
          }
        }),
      );
      this.$emit('save', this.updatedAlarmSetting);
      this.updatedAlarmSetting = {
        module: '',
        message: '',
        params: [],
        emailGroups: [],
      };
      this.wasEdited = false;
    },
    handleDialogClose() {
      if (this.wasEdited) {
        this.$confirm(this.$t('pages.details.confirm.message'), '', {
          confirmButtonText: this.$t('pages.details.yesButton'),
          cancelButtonText: this.$t('pages.details.cancelButton'),
          cancelButtonClass: 'outline-button',
        })
          .then(() => {
            this.updatedAlarmSetting = {
              module: '',
              message: '',
              params: [],
              emailGroups: [],
            };
            this.wasEdited = false;
            this.$emit('close');
            this.key += 1;
          })
          .catch(() => {
            this.$message({
              type: 'info',
              message: this.$t('pages.details.confirm.cancelMessage'),
            });
          });
      } else {
        this.updatedAlarmSetting = {
          module: '',
          message: '',
          params: [],
          emailGroups: [],
        };
        this.wasEdited = false;
        this.$emit('close');
        this.key += 1;
      }
    },
    getAlarmId(alarmIds, id) {
      if (alarmIds.length > 0 && id) {
        return alarmIds.find((v) => v.value === id).label;
      }
      return '';
    },
  },
};
</script>
<style lang="scss" scoped>
.params-title {
  color: var(--primary-secondary);
  text-transform: uppercase;
  font-weight: 600;
}
</style>
